import type { RowData } from "@tanstack/react-table";
import { EntityAvatarImage } from "@design-system/Avatar/EntityAvatarImage";
import { getEntityAvatarName } from "@helpers/entityAvatarName";
import type { DataGridEntityDisplay } from "./cellValueTypes";
import { isEntityValue } from "./cellValues";
import { SearchableText } from "./SearchableText";
import { TextCell } from "./TextCell";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

interface EntityCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    display: DataGridEntityDisplay;
}

/**
 * Entity column cell: the entity's avatar and name, or the avatar alone with
 * the name in a tooltip for narrow columns. A plain string shows as text.
 */
export const EntityCell = <TData extends RowData>({
    context,
    meta,
    display,
}: Readonly<EntityCellProps<TData>>) => {
    const value = context.getValue();
    if (!isEntityValue(value)) {
        return <TextCell context={context} meta={meta} />;
    }
    const name = getEntityAvatarName(value);
    if (display === "avatar") {
        return (
            <span
                className={styles.leading}
                data-cell-tooltip={name}
                data-cell-tooltip-always=""
            >
                <EntityAvatarImage entity={value} size="xs" />
            </span>
        );
    }
    return (
        <span className={styles.inline}>
            <EntityAvatarImage entity={value} size="xs" aria-hidden />
            <SearchableText
                text={name}
                overflow={meta.overflow}
                table={context.table}
            />
        </span>
    );
};
